import { Link, useParams, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Compass, MapPin, Store, Users } from 'lucide-react';
import type { Locality, Region } from '@shared/schema';
import type { PaginatedResponse } from '@shared/api-contracts';

import { PublicShell } from '@/components/public-shell';
import { fetchJson } from '@/lib/queryClient';
import ClassCard from '@/components/class-card';
import CreatorCard from '@/components/creator-card';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useRegionScope } from '@/hooks/useRegionScope';
import { formatSessionTime, clearPersistedGeography } from '@/lib/geography';
import { trackPublicFunnelEvent } from '@/lib/public-analytics';
import { AutoRedirect } from '@/components/auto-redirect';

interface LandingClassRecord {
  id: string;
  title: string;
  description: string;
  price: string;
  pricingMode?: string | null;
  suggestedPrice?: string | null;
  promoEnabled?: boolean | null;
  promoPrice?: string | null;
  thumbnailUrl?: string | null;
  deliveryMode?: string | null;
  enrollmentCount?: number;
  spotsLeft?: number | null;
  creator?: { id: string; displayName: string; profileImageUrl?: string | null } | null;
  category?: string | null;
  localityName?: string | null;
  venue?: {
    id?: string | null;
    name?: string | null;
    slug?: string | null;
    address?: string | null;
  } | null;
  nextSessionStart?: string | null;
  nextSessionEnd?: string | null;
  createdAt?: string | null;
}

interface LandingCreatorRecord {
  id: string;
  displayName: string;
  creatorHeadline?: string | null;
  bio?: string | null;
  profileImageUrl?: string | null;
  isVerified?: boolean;
}

interface CalendarEntry {
  id: string;
  kind: 'session' | 'event';
  title: string;
  startsAt: string;
  endsAt: string | null;
  timezone: string;
  deliveryMode?: string;
}

function useLandingQueries(region: Region | null, locality: Locality | null) {
  const geographyBasePath = locality
    ? `/api/regions/${region?.slug}/${locality.slug}`
    : `/api/regions/${region?.slug}`;

  const classesQuery = useQuery<PaginatedResponse<LandingClassRecord>>({
    queryKey: ['landing-classes', region?.slug, locality?.slug],
    queryFn: () =>
      fetchJson<PaginatedResponse<LandingClassRecord>>(
        `${geographyBasePath}/classes?limit=4&sortBy=popular`
      ),
    enabled: !!region,
    staleTime: 1000 * 60 * 5,
  });

  const creatorsQuery = useQuery<PaginatedResponse<LandingCreatorRecord>>({
    queryKey: ['landing-creators', region?.slug, locality?.slug],
    queryFn: () =>
      fetchJson<PaginatedResponse<LandingCreatorRecord>>(
        `${geographyBasePath}/creators?limit=4`
      ),
    enabled: !!region,
    staleTime: 1000 * 60 * 5,
  });

  const calendarQuery = useQuery<{ data: CalendarEntry[] }>({
    queryKey: ['landing-calendar', region?.slug, locality?.slug],
    queryFn: () =>
      fetchJson<{ data: CalendarEntry[] }>(`${geographyBasePath}/calendar?limit=4`),
    enabled: !!region,
    staleTime: 1000 * 60 * 5,
  });

  const newThisWeekQuery = useQuery<PaginatedResponse<LandingClassRecord>>({
    queryKey: ['landing-new-this-week', region?.slug, locality?.slug],
    queryFn: async () => {
      const result = await fetchJson<PaginatedResponse<LandingClassRecord>>(
        `${geographyBasePath}/classes?limit=8&sortBy=newest`
      );
      // Filter client-side to classes published within the last 7 days
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      return {
        ...result,
        data: result.data.filter(c =>
          c.createdAt ? new Date(c.createdAt).getTime() >= sevenDaysAgo : false
        ),
      };
    },
    enabled: !!region,
    staleTime: 1000 * 60 * 5,
  });

  return {
    classes: classesQuery.data?.data ?? [],
    creators: creatorsQuery.data?.data ?? [],
    calendar: calendarQuery.data?.data ?? [],
    newThisWeek: newThisWeekQuery.data?.data ?? [],
  };
}

export default function RegionLanding() {
  const { regionSlug, localitySlug } = useParams<{
    regionSlug: string;
    localitySlug?: string;
  }>();
  const [, navigate] = useLocation();
  const { region, locality, isLoading, isError } = useRegionScope(regionSlug, localitySlug);
  const { classes, creators, calendar, newThisWeek } = useLandingQueries(region, locality);

  if (isLoading) {
    return (
      <PublicShell routeKey={localitySlug ? 'locality-home' : 'region-home'}>
        <div className="page-shell flex min-h-[60vh] items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </PublicShell>
    );
  }

  if (isError || !region) {
    return (
      <PublicShell routeKey="not-found">
        <div className="page-shell-narrow flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
          <p className="editorial-kicker">Region unavailable</p>
          <h1 className="text-4xl font-semibold">We couldn't find that market.</h1>
          <p className="section-copy">
            The link may be out of date, or the region may no longer be active.
          </p>
          <AutoRedirect />
          <Button
            className="rounded-full px-5"
            onClick={() => {
              clearPersistedGeography();
              navigate('/');
            }}
          >
            Choose another region
          </Button>
        </div>
      </PublicShell>
    );
  }

  const displayName = locality?.name ?? region.name;
  const routeKey = locality ? 'locality-home' : 'region-home';
  const basePath = locality
    ? `/regions/${region.slug}/${locality.slug}`
    : `/regions/${region.slug}`;
  const timezone = locality?.timezone ?? region.timezone;
  const isLowInventory = classes.length < 2 || creators.length < 2;

  const trackLandingDiscovery = (
    destinationType: 'calendar' | 'classes' | 'creators' | 'locations'
  ) => {
    void trackPublicFunnelEvent({
      eventName: 'landing_to_discovery',
      source: routeKey,
      destinationType,
      regionSlug: region.slug,
      localitySlug: locality?.slug,
    });
  };

  return (
    <PublicShell routeKey={routeKey}>
      <section className="hero-gradient overflow-hidden border-b border-border/60">
        <div className="page-shell grid gap-10 py-14 lg:grid-cols-[1.15fr_0.85fr] lg:items-end">
          <div>
            <div className="inline-flex flex-wrap items-center gap-2">
              <Badge className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                {region.type === 'online' ? 'Online region' : locality ? 'Locality' : 'Region'}
              </Badge>
              <Badge variant="outline" className="rounded-full px-3 py-1">
                {displayName}
              </Badge>
            </div>
            <h1 className="section-title mt-5 max-w-4xl">
              {region.type === 'online'
                ? 'Join live creative classes without losing the local feel of discovery.'
                : `What's happening in ${displayName} this week.`}
            </h1>
            <p className="section-copy mt-6 max-w-2xl">
              {region.type === 'online'
                ? 'Browse live online classes from independent creators. Register for a session, join from home, and support the people teaching what they love.'
                : `Browse upcoming classes, meet the local creators teaching them, or check what's on the community calendar. Everything here is rooted in ${displayName}.`}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link href={`${basePath}/calendar`}>
                <Button
                  onClick={() => trackLandingDiscovery('calendar')}
                  className="touch-target rounded-full px-5"
                >
                  Open calendar
                </Button>
              </Link>
              <Link href={`${basePath}/classes`}>
                <Button
                  onClick={() => trackLandingDiscovery('classes')}
                  variant="outline"
                  className="touch-target rounded-full px-5"
                >
                  Browse classes
                </Button>
              </Link>
              {locality && (
                <Link href={`${basePath}/locations`}>
                  <Button
                    onClick={() => trackLandingDiscovery('locations')}
                    variant="outline"
                    className="touch-target rounded-full px-5"
                  >
                    <Store className="mr-2 h-4 w-4" />
                    Browse locations
                  </Button>
                </Link>
              )}
              <Link href={`${basePath}/creators`}>
                <Button
                  onClick={() => trackLandingDiscovery('creators')}
                  variant="ghost"
                  className="touch-target rounded-full px-5"
                >
                  Meet creators
                </Button>
              </Link>
            </div>
          </div>

          <Card className="glass-panel rounded-[2rem] border-border/60 shadow-lg">
            <CardContent className="space-y-4 p-6">
              <p className="editorial-kicker">Local context</p>
              <div className="grid gap-4 sm:grid-cols-3">
                <LandingStat icon={Compass} label="Classes" value={classes.length} />
                <LandingStat icon={Users} label="Creators" value={creators.length} />
                <LandingStat icon={CalendarDays} label="Upcoming" value={calendar.length} />
              </div>
              <p className="text-sm leading-6 text-muted-foreground">
                Your selected area stays with you as you explore classes, creators, and upcoming
                events — everything is scoped to where you are.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="page-section">
        <div className="page-shell grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-5">
            <div>
              <p className="editorial-kicker">Featured classes</p>
              <h2 className="mt-3 text-3xl font-semibold">
                {classes.length > 0
                  ? `Explore classes in ${displayName}`
                  : 'Nothing is published yet'}
              </h2>
            </div>
            {classes.length > 0 ? (
              <div className="grid gap-5 md:grid-cols-2">
                {classes.map(classItem => (
                  <ClassCard
                    key={classItem.id}
                    id={classItem.id}
                    title={classItem.title}
                    description={classItem.description}
                    price={classItem.price}
                    thumbnailUrl={classItem.thumbnailUrl}
                    category={classItem.category}
                    creator={classItem.creator}
                    deliveryMode={classItem.deliveryMode}
                    spotsLeft={classItem.spotsLeft}
                    localityName={classItem.localityName ?? displayName}
                    venue={classItem.venue}
                    venueHref={
                      locality?.slug && classItem.venue?.slug
                        ? `${basePath}/locations/${classItem.venue.slug}`
                        : null
                    }
                    sessionLabel={
                      classItem.nextSessionStart
                        ? formatSessionTime(
                            classItem.nextSessionStart,
                            classItem.nextSessionEnd ?? classItem.nextSessionStart,
                            timezone
                          )
                        : null
                    }
                    variant="comfortable"
                  />
                ))}
              </div>
            ) : (
              <FallbackModule
                title={
                  region.type === 'online'
                    ? 'Online classes are coming soon'
                    : `No classes are live in ${displayName} yet`
                }
                body={
                  region.type === 'online'
                    ? 'Keep this region selected and check the calendar later, or explore another market for in-person classes.'
                    : "We'll keep showing this market context, but the next best move is to check nearby creators and regional calendar activity."
                }
                actions={[
                  { href: `${basePath}/calendar`, label: 'View calendar' },
                  { href: `${basePath}/creators`, label: 'Browse creators', secondary: true },
                ]}
              />
            )}
          </div>

          <div className="space-y-5">
            <div>
              <p className="editorial-kicker">Upcoming calendar</p>
              <h2 className="mt-3 text-3xl font-semibold">
                See what&apos;s going on in your community
              </h2>
            </div>
            <Card className="rounded-[1.75rem] border-border/70 shadow-sm">
              <CardContent className="space-y-4 p-6">
                {calendar.length > 0 ? (
                  calendar.map(entry => (
                    <div
                      key={entry.id}
                      className="rounded-[1.25rem] border border-border/60 bg-muted/35 p-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium text-foreground">{entry.title}</p>
                        <Badge variant="outline" className="rounded-full px-3 py-1">
                          {entry.kind === 'session' ? 'Class session' : 'Event'}
                        </Badge>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        {formatSessionTime(
                          entry.startsAt,
                          entry.endsAt ?? entry.startsAt,
                          entry.timezone
                        )}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm leading-6 text-muted-foreground">
                    Nothing scheduled yet. Check back soon — this is the first place new sessions
                    and events will appear.
                  </p>
                )}
                <Link href={`${basePath}/calendar`}>
                  <Button
                    onClick={() => trackLandingDiscovery('calendar')}
                    variant="outline"
                    className="touch-target mt-2 rounded-full px-5"
                  >
                    View full calendar
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <div>
              <p className="editorial-kicker">Featured creators</p>
              <h2 className="mt-3 text-3xl font-semibold">The people behind the classes</h2>
            </div>
            {creators.length > 0 ? (
              <div className="grid gap-5">
                {creators.map(creator => (
                  <CreatorCard
                    key={creator.id}
                    id={creator.id}
                    displayName={creator.displayName}
                    creatorHeadline={creator.creatorHeadline ?? 'Creative educator'}
                    bio={creator.bio}
                    profileImageUrl={creator.profileImageUrl}
                    isVerified={creator.isVerified}
                    localityName={displayName}
                    variant="compact"
                  />
                ))}
              </div>
            ) : (
              <FallbackModule
                title={
                  isLowInventory ? 'More creators are on the way' : 'Creators are still being added'
                }
                body="Independent educators and makers are joining this market. In the meantime, browse classes or apply to teach."
                actions={[
                  { href: '/creator/apply', label: 'Create with Kalovate' },
                  { href: `${basePath}/classes`, label: 'Browse all classes', secondary: true },
                ]}
              />
            )}
          </div>
        </div>
      </section>

      {newThisWeek.length >= 2 && (
        <section className="page-section border-t border-border/60">
          <div className="page-shell space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="editorial-kicker">Fresh off the press</p>
                <h2 className="mt-3 text-3xl font-semibold">New this week</h2>
              </div>
              <Link href={`${basePath}/classes?sortBy=newest`}>
                <Button variant="outline" className="rounded-full px-5">
                  See all
                </Button>
              </Link>
            </div>
            <div className="flex gap-5 overflow-x-auto overscroll-x-contain pb-2 -mx-4 px-4 md:-mx-0 md:px-0">
              {newThisWeek.map(classItem => (
                <div key={classItem.id} className="w-64 shrink-0 md:w-72">
                  <ClassCard
                    id={classItem.id}
                    title={classItem.title}
                    description={classItem.description}
                    price={classItem.price}
                    pricingMode={classItem.pricingMode}
                    suggestedPrice={classItem.suggestedPrice}
                    promoEnabled={classItem.promoEnabled}
                    promoPrice={classItem.promoPrice}
                    thumbnailUrl={classItem.thumbnailUrl}
                    category={classItem.category}
                    creator={classItem.creator}
                    deliveryMode={classItem.deliveryMode}
                    spotsLeft={classItem.spotsLeft}
                    localityName={classItem.localityName ?? displayName}
                    venue={classItem.venue}
                    venueHref={
                      locality?.slug && classItem.venue?.slug
                        ? `${basePath}/locations/${classItem.venue.slug}`
                        : null
                    }
                    variant="compact"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </PublicShell>
  );
}

function LandingStat({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof MapPin;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-[1.25rem] border border-border/70 bg-card/80 p-4">
      <Icon className="h-5 w-5 text-primary" />
      <p className="mt-3 text-2xl font-semibold text-foreground">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function FallbackModule({
  title,
  body,
  actions,
}: {
  title: string;
  body: string;
  actions: Array<{ href: string; label: string; secondary?: boolean }>;
}) {
  return (
    <Card className="rounded-[1.75rem] border-border/70 shadow-sm">
      <CardContent className="space-y-5 p-6">
        <div>
          <p className="editorial-kicker">Fallback module</p>
          <h3 className="mt-3 text-2xl font-semibold">{title}</h3>
        </div>
        <p className="text-sm leading-6 text-muted-foreground">{body}</p>
        <div className="flex flex-wrap gap-3">
          {actions.map(action => (
            <Link key={action.href} href={action.href}>
              <Button
                variant={action.secondary ? 'outline' : 'default'}
                className="touch-target rounded-full px-5"
              >
                {action.label}
              </Button>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
